/**
 * Wallet and blockchain configuration
 */

import { baseSepolia, base } from 'viem/chains';
import type { Chain, Address } from 'viem';

// Environment-based configuration
// Vite statically replaces import.meta.env.VITE_* at build time
// We must use direct property access (not bracket notation) for this to work
const ENV = {
  CHAIN_ID: parseInt(import.meta.env.VITE_CHAIN_ID || '84532'),
  MINE_OR_DIE_ADDRESS: (import.meta.env.VITE_MINE_OR_DIE_ADDRESS || undefined) as Address | undefined,
  USDC_ADDRESS: (import.meta.env.VITE_USDC_ADDRESS || undefined) as Address | undefined,
  PAYMASTER_URL: import.meta.env.VITE_PAYMASTER_URL || undefined,
  USE_SMART_WALLET: (import.meta.env.VITE_USE_SMART_WALLET || 'true') === 'true',
};

// Debug: log env vars on load
console.log('Wallet config loaded:');
console.log('  VITE_USDC_ADDRESS:', import.meta.env.VITE_USDC_ADDRESS);
console.log('  VITE_MINE_OR_DIE_ADDRESS:', import.meta.env.VITE_MINE_OR_DIE_ADDRESS);
console.log('  ENV.USDC_ADDRESS:', ENV.USDC_ADDRESS);
console.log('  ENV.MINE_OR_DIE_ADDRESS:', ENV.MINE_OR_DIE_ADDRESS);

/**
 * Get chain configuration
 */
export function getChain(): Chain {
  if (ENV.CHAIN_ID === base.id) return base;
  return baseSepolia;
}

/**
 * Contract addresses
 */
export const CONTRACTS = {
  mineOrDie: ENV.MINE_OR_DIE_ADDRESS,
  usdc: ENV.USDC_ADDRESS,
} as const;

/**
 * Paymaster URL for gas sponsorship
 */
export const PAYMASTER_URL = ENV.PAYMASTER_URL;

/**
 * Whether to use Smart Wallet (with AA) by default
 */
export const USE_SMART_WALLET = ENV.USE_SMART_WALLET;

/**
 * Entry fee in USDC (6 decimals)
 */
export const ENTRY_FEE = 1_000_000n; // $1 USDC

/**
 * Default funding amount for initial deposits
 */
export const DEFAULT_FUND_AMOUNT = 10_000_000n; // $10 USDC

/**
 * USDC token ABI (minimal for approve/transfer/balanceOf)
 */
export const USDC_ABI = [
  {
    name: 'approve',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'balanceOf',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'allowance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ type: 'uint256' }],
  },
] as const;

/**
 * MineOrDie contract ABI (simplified single contract)
 */
export const MINE_OR_DIE_ABI = [
  // Player functions
  {
    name: 'fund',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: [],
  },
  {
    name: 'withdraw',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: [],
  },
  // View functions
  {
    name: 'balances',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'currentlyWagered',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'availableBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'getMatch',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'matchId', type: 'bytes32' }],
    outputs: [
      { name: 'players', type: 'address[]' },
      { name: 'wagerPerPlayer', type: 'uint256' },
      { name: 'totalPot', type: 'uint256' },
      { name: 'settled', type: 'bool' },
      { name: 'cancelled', type: 'bool' },
    ],
  },
  {
    name: 'devFeeBps',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  // Admin view functions
  {
    name: 'accumulatedDevFees',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'admin',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address' }],
  },
] as const;

// Legacy escrow ABI - deprecated, keeping for backwards compatibility
// Note: stake/claim/hasStaked/claimableReward are no longer in the new contract
export const ESCROW_ABI = [
  ...MINE_OR_DIE_ABI,
  // Legacy functions that no longer exist - kept for type compatibility only
  {
    name: 'stake',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: [],
  },
  {
    name: 'claim',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [],
    outputs: [],
  },
  {
    name: 'hasStaked',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'claimableReward',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'player', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  {
    name: 'settled',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'bool' }],
  },
  {
    name: 'settle',
    type: 'function',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'winners', type: 'address[]' },
      { name: 'amounts', type: 'uint256[]' },
      { name: 'v', type: 'uint8' },
      { name: 'r', type: 'bytes32' },
      { name: 's', type: 'bytes32' },
    ],
    outputs: [],
  },
] as const;
